/*
 * r16q (2026-10-27) - the V3 side of the merged server r16q's chat request guard (r16pdc rounds 1 + 2, its release README
 * section 4; the server's words are in src/lib/__fixtures__/chatGuard.r16q.json, read from NSB-r16q e77bcae). Every part draws
 * only what the server sends: a server without the guard (r16pd / r16sd, or -Dnecro.server.chats.folderGuard=false /
 * -Dnecro.server.chats.inboundOutOfView=false) sends no waitingReason / waitingMessage and no `message` on a reply, and then the
 * page is the r16sd page except for the parts below that key on a CODE only (those are the same on any server).
 *
 * ONE master switch plus one per part, all ON by default (owner rule: features ship on):
 *
 *   master            necro.ui.r16q                     REACT_APP_NECRO_UI_R16Q
 *   waitingLine       necro.ui.r16q.waitingLine         REACT_APP_NECRO_UI_R16Q_WAITINGLINE        a queued reply's waiting line from the server's
 *                                                                                                waitingReason / waitingMessage (S4)
 *   outOfView         necro.ui.r16q.outOfView           REACT_APP_NECRO_UI_R16Q_OUTOFVIEW          a FAILED inbound_out_of_view reply: the server's
 *                                                                                                sentence (FAIL_CODE_WORDS copy) and no "Queue again"
 *   reloadNotRuledOut necro.ui.r16q.reloadNotRuledOut   REACT_APP_NECRO_UI_R16Q_RELOADNOTRULEDOUT  a send refused request_not_ruled_out re-reads the
 *                                                                                                conversation (RELOAD_ON_CODES), so the closed
 *                                                                                                composer says why at once
 *   apiHelp           necro.ui.r16q.apiHelp             REACT_APP_NECRO_UI_R16Q_APIHELP            the API docs lines for waiting_reason / fail_message
 *
 *   build time: REACT_APP_NECRO_UI_R16Q=off (also 0 / false / no), or one part's variable;
 *   run time:   localStorage['necro.ui.r16q'] = 'off' (or 'necro.ui.r16q.<part>' = 'off') in one browser, then reload; 'on' there
 *               overrides a build-time off. The master OFF turns every part off (= the r16sd bundle 83fc0aa).
 */

const OFF_WORDS = new Set(['off', '0', 'false', 'no']);
const ON_WORDS = new Set(['on', '1', 'true', 'yes']);
const norm = (v) => (v === null || v === undefined ? '' : String(v).trim().toLowerCase());

export const R16Q_TAG = '[R16Q-UI]';

export const R16Q_PARTS = Object.freeze(['waitingLine', 'outOfView', 'reloadNotRuledOut', 'apiHelp']);

/* Spelled out one by one: CRA inlines only a literal process.env.REACT_APP_X reference. */
function envFor(part) {
  switch (part) {
    case 'master': return process.env.REACT_APP_NECRO_UI_R16Q;
    case 'waitingLine': return process.env.REACT_APP_NECRO_UI_R16Q_WAITINGLINE;
    case 'outOfView': return process.env.REACT_APP_NECRO_UI_R16Q_OUTOFVIEW;
    case 'reloadNotRuledOut': return process.env.REACT_APP_NECRO_UI_R16Q_RELOADNOTRULEDOUT;
    case 'apiHelp': return process.env.REACT_APP_NECRO_UI_R16Q_APIHELP;
    default: return undefined;
  }
}

const storageKey = (part) => (part === 'master' ? 'necro.ui.r16q' : `necro.ui.r16q.${part}`);
const envName = (part) => (part === 'master' ? 'REACT_APP_NECRO_UI_R16Q' : `REACT_APP_NECRO_UI_R16Q_${part.toUpperCase()}`);

function readStorage(key) {
  // Private windows, blocked site data and thumbnail renderers throw on the accessor itself.
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

/** Pure: one switch from its build value and its browser value (the r14Switch / doctorSwitch rule). */
export function resolveR16qSwitch(part, envValue, storageValue) {
  const key = storageKey(part);
  const s = norm(storageValue);
  if (OFF_WORDS.has(s)) return { enabled: false, source: `localStorage ${key}=${s}` };
  if (ON_WORDS.has(s)) return { enabled: true, source: `localStorage ${key}=${s}` };
  const e = norm(envValue);
  if (OFF_WORDS.has(e)) return { enabled: false, source: `build ${envName(part)}=${e}` };
  return { enabled: true, source: e ? `build ${envName(part)}=${e}` : 'default' };
}

let logged = false;

/** Every switch as the page sees it now: { master, parts: {part: bool} }. Logs the decision once per page load. */
export function r16qSwitches() {
  const master = resolveR16qSwitch('master', envFor('master'), readStorage(storageKey('master')));
  const parts = {};
  const off = [];
  R16Q_PARTS.forEach((p) => {
    const r = resolveR16qSwitch(p, envFor(p), readStorage(storageKey(p)));
    parts[p] = master.enabled && r.enabled;
    if (!r.enabled) off.push(`${p} (${r.source})`);
  });
  if (!logged) {
    logged = true;
    try {
      // eslint-disable-next-line no-console
      console.info(`${R16Q_TAG} chat request guard UI ${master.enabled ? 'ON' : 'OFF'} (${master.source}); parts off: ${off.length ? off.join(', ') : 'none'}`);
    } catch (e) { /* a console that throws must not break the page */ }
  }
  return { master: master.enabled, parts };
}

/** true when the master and this part are both on. */
export function r16qUiOn(part) {
  const s = r16qSwitches();
  return !!(s.master && s.parts[part]);
}

/** Tests only: forget that the decision was logged. */
export function resetR16qSwitchLog() {
  logged = false;
}
